import { Box, Chip, Typography, useMediaQuery } from "@mui/material";
import { useCallback } from "react";
import { CardDTO } from "../interface/GameTypes";
import { toCardCode } from "../interface/GameTypes";
import { PlayingCardFace } from "./PlayingCardFace";

const BASE_POINTS: Record<string, number> = {
  Ace: 11, Ten: 10, King: 3, Queen: 2, Jack: 1, Nine: 0,
};
const cardPoints = (card: CardDTO, trumpSuit: string | null): number => {
  if (trumpSuit && card.suit === trumpSuit) {
    if (card.rank === "Jack") return 20;
    if (card.rank === "Nine") return 14;
  }
  return BASE_POINTS[card.rank] ?? 0;
};

interface PlayedCard {
  playerIndex: number;
  card: CardDTO;
}

interface Props {
  hand: CardDTO[];
  /** While first deal shuffle runs on table — hide hand cards entirely. */
  hideHandUntilDealShown?: boolean;
  isMyTurn: boolean;
  isPlayingPhase: boolean;
  ledSuit: string | null;
  trumpSuit: string | null;
  currentRound: PlayedCard[];
  onPlayCard: (card: CardDTO) => void;
  isCallerTeam?: boolean;
  showPoints?: boolean;
  trumpRippleTick?: number;
  compactLandscape?: boolean;
  sidebarCompact?: boolean;
}

/** Trump rank order (low → high): 10 < Q < K < A < 9 < J */
const TRUMP_RANK_ORDER = ["Ten", "Queen", "King", "Ace", "Nine", "Jack"];

const isLegal = (
  card: CardDTO,
  hand: CardDTO[],
  ledSuit: string | null,
  trumpSuit: string | null,
  currentRound: PlayedCard[]
): boolean => {
  if (ledSuit === null || trumpSuit === null) return true;

  if (ledSuit === trumpSuit) {
    const hasTrump = hand.some((c) => c.suit === trumpSuit);
    if (hasTrump) return card.suit === trumpSuit;
    return true;
  }

  if (card.suit === ledSuit) return true;

  if (card.suit === trumpSuit) {
    const trumpRanksInRound = currentRound
      .filter((pc) => pc.card.suit === trumpSuit)
      .map((pc) => TRUMP_RANK_ORDER.indexOf(pc.card.rank));
    const highestTrumpRank =
      trumpRanksInRound.length > 0 ? Math.max(...trumpRanksInRound) : -1;

    if (highestTrumpRank === -1) return true;
    if (TRUMP_RANK_ORDER.indexOf(card.rank) >= highestTrumpRank) return true;

    const hasLedSuit = hand.some((c) => c.suit === ledSuit);
    const hasValidTrump = hand.some(
      (c) =>
        c.suit === trumpSuit &&
        TRUMP_RANK_ORDER.indexOf(c.rank) >= highestTrumpRank
    );
    const hasNonTrump = hand.some((c) => c.suit !== trumpSuit);
    return !hasLedSuit && !hasValidTrump && !hasNonTrump;
  }

  const hasLedSuit = hand.some((c) => c.suit === ledSuit);
  return !hasLedSuit;
};

const CARD_W = 64;

function cardKey(c: CardDTO) {
  return `${c.suit}-${c.rank}`;
}

export const PlayerHand = ({
  hand,
  hideHandUntilDealShown = false,
  isMyTurn,
  isPlayingPhase,
  ledSuit,
  trumpSuit,
  currentRound,
  onPlayCard,
  isCallerTeam,
  showPoints = true,
  trumpRippleTick = 0,
  compactLandscape = false,
  sidebarCompact = false,
}: Props) => {
  const hideCards = hideHandUntilDealShown;
  const wideScreen = useMediaQuery("(min-width:1024px)");
  const cardWidth = compactLandscape ? 46 : wideScreen ? 80 : CARD_W;
  const effectiveShowPoints = compactLandscape ? false : showPoints;

  const onCardActivate = useCallback(
    (card: CardDTO, clickable: boolean) => {
      if (!clickable) return;
      onPlayCard(card);
    },
    [onPlayCard]
  );

  return (
    <Box
      data-testid="player-hand"
      sx={{
        position: "relative",
        ...(isCallerTeam !== undefined && {
          borderTop: isCallerTeam ? "3px solid #66bb6a" : "3px solid #ef5350",
          pt: 1,
        }),
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: compactLandscape ? 0.5 : 1, mb: compactLandscape ? 0.5 : 1 }}>
        {isCallerTeam !== undefined && !compactLandscape && (
          <Chip
            label={isCallerTeam ? "CALLER'S TEAM" : "OPPOSITION"}
            size="small"
            sx={{
              backgroundColor: isCallerTeam ? "rgba(102,187,106,0.25)" : "rgba(239,83,80,0.25)",
              color: isCallerTeam ? "#66bb6a" : "#ef5350",
              border: `1px solid ${isCallerTeam ? "#66bb6a" : "#ef5350"}`,
              fontWeight: "bold",
              fontSize: "0.72rem",
              letterSpacing: 0.4,
              height: 22,
            }}
          />
        )}
        <Typography
          variant="subtitle2"
          color="white"
          sx={{ textAlign: "center", fontSize: compactLandscape ? "0.85rem" : undefined }}
        >
          Your Hand
        </Typography>
        {!compactLandscape && isMyTurn ? (
          <Chip
            label="YOUR TURN — play a card!"
            size="small"
            sx={{
              backgroundColor: "#FFD700",
              color: "#000",
              fontWeight: "bold",
              fontSize: "0.75rem",
              animation: "pulse-chip 0.8s ease-in-out infinite alternate",
              "@keyframes pulse-chip": {
                from: { boxShadow: "0 0 0 0 rgba(255, 215, 0, 0.8)" },
                to: { boxShadow: "0 0 0 8px rgba(255, 215, 0, 0)" },
              },
            }}
          />
        ) : !compactLandscape && isPlayingPhase ? (
          <Chip
            label="Waiting for your turn…"
            size="small"
            sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.6)", fontSize: "0.7rem" }}
          />
        ) : null}
      </Box>

      <Box
        key={trumpRippleTick > 0 ? `hand-ripple-${trumpRippleTick}` : "hand-row"}
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: sidebarCompact ? "center" : "flex-end",
          alignContent: sidebarCompact ? "center" : compactLandscape ? "stretch" : "flex-end",
          gap: compactLandscape ? { xs: 0.4, sm: 0.45 } : { xs: 0.5, sm: 0.75 },
          width: "100%",
          maxWidth: "100%",
          minHeight: sidebarCompact
            ? { xs: 64, sm: 72 }
            : compactLandscape
              ? { xs: 124, sm: 132 }
              : { xs: 150, sm: 160 },
          pt: sidebarCompact ? 0 : compactLandscape ? 0.25 : 1,
          px: compactLandscape ? { xs: 0.25, sm: 0.5 } : { xs: 0.5, sm: 1 },
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: "visible",
          pointerEvents: hideCards || !isMyTurn ? "none" : "auto",
          ...(trumpRippleTick > 0 && {
            animation: "addataHandTrumpRipple 0.55s ease-out",
            "@keyframes addataHandTrumpRipple": {
              "0%, 100%": { filter: "brightness(1)" },
              "45%": { filter: "brightness(1.12) drop-shadow(0 0 12px rgba(255,215,0,0.45))" },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }),
        }}
      >
        {hideCards ? null : (
          <>
            {hand.map((card, i) => {
              const { value, suit } = toCardCode(card);
              const legal = isLegal(card, hand, ledSuit, trumpSuit, currentRound);
              const clickable = isMyTurn && legal;
              const pts = cardPoints(card, trumpSuit);
              const isTrump = trumpSuit != null && card.suit === trumpSuit;
              const instanceKey = `${i}-${cardKey(card)}`;

              return (
                <Box
                  key={instanceKey}
                  data-testid="hand-card"
                  data-suit={card.suit}
                  data-rank={card.rank}
                  data-clickable={String(clickable)}
                  sx={{
                    flexShrink: 0,
                    ...(compactLandscape && {
                      flex: "0 0 calc((100% - 1.35rem) / 4)",
                      display: "flex",
                      justifyContent: "center",
                    }),
                    zIndex: 20 + i,
                    cursor: clickable ? "pointer" : "default",
                  }}
                  onClick={clickable ? () => onCardActivate(card, true) : undefined}
                >
                  <Box
                    sx={{
                      filter: isMyTurn && !legal ? "grayscale(60%)" : "none",
                      opacity: isMyTurn && !legal ? 0.35 : 1,
                      "&:hover": clickable
                        ? {
                            filter: isMyTurn && !legal ? "grayscale(60%)" : "brightness(1.06)",
                          }
                        : {},
                    }}
                  >
                    <PlayingCardFace rankCode={value} suitCode={suit} widthPx={cardWidth} />
                  </Box>
                  {effectiveShowPoints && (
                    <Box
                      sx={{
                        mt: 0.5,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        backgroundColor: isTrump ? "rgba(255,215,0,0.25)" : "rgba(255,255,255,0.15)",
                        border: isTrump ? "1px solid rgba(255,215,0,0.5)" : "1px solid rgba(255,255,255,0.25)",
                        pointerEvents: "none",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          fontWeight: "bold",
                          color: isTrump ? "#FFD700" : "#fff",
                          lineHeight: 1.2,
                          textAlign: "center",
                        }}
                      >
                        {pts}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </Box>
  );
};
